/** coordinate systems:
 - browser coordinates can only be obtained by DOM events/functions and coordinate transformation:
     - viewport: a position in the browser viewport, independent of the scroll position
     - window:   a position in the browser window, changes with the scroll position
 - window len:   a distance between two browser coordinates
 - card:         scaled by zoom, relative to a certain card
 - board:        scaled by zoom, limited by border for the top left corner of a card, used to draw card
 - relative:     0..1 for the center of a card, used for backend
 */
export type ViewportCoordinate = Coordinate & { _type: "viewport" };
export type WindowCoordinate = Coordinate & { _type: "window" };
export type WindowLen = Coordinate & { _type: "windowLen" };
export type BoardCoordinate = Coordinate & { _type: "board" };
export type RelativeCoordinate = Coordinate & { _type: "relative" };
export type AnyCoordinate =
  | ViewportCoordinate
  | WindowCoordinate
  | WindowLen
  | BoardCoordinate
  | RelativeCoordinate;

interface Coordinate {
  x: number;
  y: number;
}

export type CoordinateComponent = "x" | "y";

export interface Rectangle<T extends AnyCoordinate> {
  p0: T;
  p1: T;
}

export interface SizedRectangle<T extends AnyCoordinate = WindowLen> {
  p0: T;
  size: T;
}

export type Line<T extends AnyCoordinate> = Rectangle<T>;

export type LineComponent = "p0" | "p1";

export function oppositeLineComponent(c: LineComponent) {
  return c === "p0" ? "p1" : "p0";
}

export function initViewportCoord(): ViewportCoordinate {
  return { x: 0, y: 0 } as ViewportCoordinate;
}

export function initWindowCoord(): WindowCoordinate {
  return { x: 0, y: 0 } as WindowCoordinate;
}

export function initBoardCoord(): BoardCoordinate {
  return { x: 0, y: 0 } as BoardCoordinate;
}

export function windowLen(x: number, y: number): WindowLen {
  return { x, y } as WindowLen;
}

export function relativeCoord(x: number, y: number): RelativeCoordinate {
  return { x, y } as RelativeCoordinate;
}
